import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { MasterConfig, MasterConfigParams } from '../../interfaces/master-config.interface';
import { PageResponse } from '../../interfaces/page-response.interface';
import { CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { MasterConfigService } from './master-config.service';

@Injectable({
  providedIn: 'root'
})
export class MasterConfigMockService extends MasterConfigService {
  private mockData: MasterConfig[] = [
    { id: 1, uniqueId: 'uuid-1', category: 'teamsize', name: '1-10 Employees', code: 'S_1_10', active: true, description: '1-10 Employees' },
    { id: 2, uniqueId: 'uuid-2', category: 'teamsize', name: '11-50 Employees', code: 'S_11_50', active: true, description: '11-50 Employees' },
    { id: 3, uniqueId: 'uuid-3', category: 'teamsize', name: '51-200 Employees', code: 'S_51_200', active: true, description: '51-200 Employees' },
    // States
    { id: 4, uniqueId: 'st-ka', category: 'STATE', name: 'Karnataka', code: 'KA', active: true, description: 'Karnataka' },
    { id: 5, uniqueId: 'st-tn', category: 'STATE', name: 'Tamil Nadu', code: 'TN', active: true, description: 'Tamil Nadu' },
    { id: 6, uniqueId: 'st-mh', category: 'STATE', name: 'Maharashtra', code: 'MH', active: true, description: 'Maharashtra' },
    // Cities
    { id: 7, uniqueId: 'ct-blr', category: 'CITY', name: 'Bangalore', code: 'BLR', active: true, description: 'st-ka' },
    { id: 8, uniqueId: 'ct-mys', category: 'CITY', name: 'Mysore', code: 'MYS', active: true, description: 'st-ka' },
    { id: 9, uniqueId: 'ct-chn', category: 'CITY', name: 'Chennai', code: 'CHN', active: true, description: 'st-tn' },
    { id: 10, uniqueId: 'ct-cbe', category: 'CITY', name: 'Coimbatore', code: 'CBE', active: true, description: 'st-tn' },
    { id: 11, uniqueId: 'ct-mum', category: 'CITY', name: 'Mumbai', code: 'MUM', active: true, description: 'st-mh' },
    { id: 12, uniqueId: 'ct-pun', category: 'CITY', name: 'Pune', code: 'PUN', active: true, description: 'st-mh' },
    // Countriesx 
    { id: 13, uniqueId: 'India', category: 'COUNTRY', name: 'India', code: 'IN', active: true, description: 'India' },
    { id: 14, uniqueId: 'cnt-usa-001', category: 'COUNTRY', name: 'USA', code: 'US', active: true, description: 'USA' },
    { id: 15, uniqueId: 'cnt-uk-001', category: 'COUNTRY', name: 'UK', code: 'UK', active: true, description: 'UK' },
    
    // Industries
    { id: 16, uniqueId: 'ind-it', category: 'INDUSTRY', name: 'Information Technology', active: true, description: 'Software and Services' },
    { id: 17, uniqueId: 'ind-fin', category: 'INDUSTRY', name: 'Finance', active: true, description: 'Banking and Investment' },
    { id: 18, uniqueId: 'ind-health', category: 'INDUSTRY', name: 'Healthcare', active: true, description: 'Medical and Health Services' },
    
    // Roles (referencing Industry uniqueId)
    { id: 19, uniqueId: 'role-se', category: 'ROLE', name: 'Software Engineer', active: true, description: 'Builds software', industryUniqueId: 'ind-it' },
    { id: 20, uniqueId: 'role-da', category: 'ROLE', name: 'Data Analyst', active: true, description: 'Analyzes data', industryUniqueId: 'ind-it' },
    { id: 21, uniqueId: 'role-fa', category: 'ROLE', name: 'Financial Analyst', active: true, description: 'Analyzes finances', industryUniqueId: 'ind-fin' },
    { id: 22, uniqueId: 'role-nurse', category: 'ROLE', name: 'Nurse', active: true, description: 'Healthcare professional', industryUniqueId: 'ind-health' },
  ];

  getConfigMaster(params: MasterConfigParams): Observable<PageResponse<MasterConfig>> {
    let filtered = [...this.mockData];
    if (params.category) {
      filtered = filtered.filter(item => item.category === params.category);
    }
    
    if (params.searchTerm) {
      const term = params.searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name?.toLowerCase().includes(term) || item.code?.toLowerCase().includes(term))
      );
    }

    const start = params.page * params.size;
    const paginatedItems = filtered.slice(start, start + params.size);

    return of({
      content: paginatedItems,
      page: {
        number: params.page,
        size: params.size,
        totalElements: filtered.length,
        totalPages: Math.ceil(filtered.length / params.size)
      }
    }).pipe(delay(500));
  }

  override findAll(commonSearchParam: CommonSearchParam): Observable<PageResponse<MasterConfig>> {
    return this.getConfigMaster({
      page: commonSearchParam.page,
      size: commonSearchParam.size,
      category: 'teamsize'
    });
  }

  override find(id: string): Observable<MasterConfig> {
    const item = this.mockData.find(d => d.id?.toString() === id || d.uniqueId === id);
    if (!item) {
      throw new Error(`Master config with id ${id} not found.`);
    }
    return of(item).pipe(delay(500));
  }

  override create(data: Partial<MasterConfig>): Observable<MasterConfig> {
    const newItem: MasterConfig = {
      ...data as MasterConfig,
      id: this.mockData.length + 1,
      uniqueId: `uuid-${this.mockData.length + 1}`
    };
    this.mockData.push(newItem);
    return of(newItem).pipe(delay(500));
  }

  override update(id: string, data: Partial<MasterConfig>): Observable<MasterConfig> {
    const index = this.mockData.findIndex(d => d.id?.toString() === id || d.uniqueId === id);
    if (index === -1) {
      throw new Error(`Master config with id ${id} not found.`);
    }
    this.mockData[index] = { ...this.mockData[index], ...data as MasterConfig };
    return of(this.mockData[index]).pipe(delay(500));
  }

  override delete(id: string): Observable<void> {
    this.mockData = this.mockData.filter(d => d.id?.toString() !== id && d.uniqueId !== id);
    return of(undefined).pipe(delay(500));
  }
}
